/*
 * A BACK/FORWARD TRAIL — the arithmetic only, so it can be reasoned about on its own.
 *
 * The AI panel navigates a recursive structure: a thread spawns agents, each agent has its own
 * thread and its own canvas, and those spawn again. Moving through it needs the ordinary browser
 * gesture in both directions, and the rules are small enough to look obvious and easy enough to get
 * subtly wrong — truncate-on-visit, the bounds at each end, and the difference between "go
 * somewhere new" and "restore a place I have already been" (the second must not record, or the
 * reader can never get past the place they are returning to).
 *
 * Immutable, so it drops straight into a reactive property: every operation returns a new trail and
 * a component can hold it as state without a manual invalidation.
 */
export const emptyTrail = () => ({ items: [], at: -1 });
/**
 * Go somewhere NEW.
 *
 * Anything ahead of the current position is discarded, exactly as a browser does: having stepped
 * back and then gone somewhere else, the branch you abandoned is not somewhere "forward" any more.
 */
export const visit = (t, place) => ({
    items: [...t.items.slice(0, t.at + 1), place],
    at: t.at + 1,
});
export const canBack = (t) => t.at > 0;
export const canForward = (t) => t.at >= 0 && t.at < t.items.length - 1;
/** Step back, or stay put at the start — never off the end. */
export const back = (t) => (canBack(t) ? { items: t.items, at: t.at - 1 } : t);
/** Step forward, or stay put at the tip. */
export const forward = (t) => (canForward(t) ? { items: t.items, at: t.at + 1 } : t);
export const current = (t) => (t.at >= 0 ? t.items[t.at] : undefined);
/**
 * Rewrite the place you are ALREADY at.
 *
 * Not a move: nothing is pushed, nothing ahead is discarded, and back/forward land exactly where
 * they would have. It exists for the one-shot parts of a place — "and when you get there, open it" —
 * which are true the first time the reader arrives and false every time afterwards. Left on the
 * trail they fire again on the way back, which is a surface that will not let you stand on it.
 */
export const replace = (t, place) => t.at < 0 ? t : { items: [...t.items.slice(0, t.at), place, ...t.items.slice(t.at + 1)], at: t.at };
/*
 * THE STACK VIEW'S WINDOW.
 *
 * The panel draws the trail as columns — the place the reader is in on the left, and the places it
 * was reached through beside it, up to `n` of them. Newest first, because the leftmost column is the
 * one being worked in and the rest are its context. Nothing here is a new kind of state: the columns
 * ARE the trail, so closing one and stepping back are the same arithmetic as visiting.
 */
/** The last `n` places up to and including the current one, newest first. */
export const window = (t, n) => t.at < 0 || n <= 0 ? [] : t.items.slice(Math.max(0, t.at - n + 1), t.at + 1).reverse();
/** Leave the current place FOR GOOD — closing the left column. Unlike `back`, the place is dropped,
 *  and the forward branch with it: a closed column is not somewhere forward can return to. */
export const pop = (t) => (t.at < 0 ? t : { items: t.items.slice(0, t.at), at: t.at - 1 });
/** Close a place BEHIND the current one — a context column. The current place stays current and the
 *  forward branch is kept; only that one step of history is removed. Index outside the past: no-op. */
export const closeAt = (t, index) => index < 0 || index >= t.at ? t : { items: [...t.items.slice(0, index), ...t.items.slice(index + 1)], at: t.at - 1 };
/**
 * VISIT, WITH EACH PLACE APPEARING ONCE.
 *
 * A plain `visit` is browser history: go to a, then b, then a again and the trail holds a, b, a.
 * That is correct for a back button and wrong for a stack of columns, where the trail IS what is on
 * screen — the same thread would be drawn in two columns at once, which is two live surfaces over
 * one conversation, two composers writing to it, and a duplicate key in the column list besides.
 *
 * So a place already behind the reader MOVES to the front instead of being copied. Found by the
 * random walk in `ai-stack-stress.test.ts`, not by anybody using it: it needs a place to be opened,
 * left behind by two more, and then opened again, which is a sequence nobody writes down.
 *
 * `sameAs` rather than identity, because a place is a value: two `tree` places for one root are the
 * same column even when one carries a fresher "where I came from".
 */
export const visitUnique = (t, place, sameAs) => visitOrdered(t, place, sameAs, () => 0);
/**
 * …AND A NEW PLACE LANDS WHERE ITS DEPTH PUTS IT, not always on the end.
 *
 * The columns read right-to-left as a hierarchy: the thread that started the work on the right, what
 * it spawned to its left, what THAT spawned further left again. Appending always put the newest on
 * the far left, which is correct exactly when the newest is also the deepest — the usual case, since
 * you reach an agent by opening it from its parent.
 *
 * It is wrong the moment it is not. Two unrelated threads open, then a worker belonging to the
 * RIGHT-hand one: it went to the far left, across a thread it has nothing to do with, and the row
 * stopped describing anything. So the place is inserted after the last one at or below its own
 * depth, which puts it beside its own family and leaves everything else where it was.
 *
 * INSERTION ONLY. Nothing already on the trail moves — a reader who arranged the columns by hand has
 * arranged them, and a stack that re-sorted itself to stay tidy would be taking that away every time
 * they opened something.
 *
 * Depth comes from the caller because only the panel can ask the run tree. A caller with no notion
 * of depth passes zero for everything, which is an append and is what `visitUnique` does.
 */
export const visitOrdered = (t, place, sameAs, depthOf) => {
    const kept = t.items.slice(0, t.at + 1).filter((x) => !sameAs(x, place));
    const d = depthOf(place);
    let i = kept.length;
    while (i > 0 && depthOf(kept[i - 1]) > d)
        i--;
    return { items: [...kept.slice(0, i), place, ...kept.slice(i)], at: kept.length };
};
//# sourceMappingURL=nav-trail.js.map